import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LayoutGrid, Loader2, Lock, Mail, ShieldCheck } from 'lucide-react';

export default function Login() {
  const { login, user, loading } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const brandName = settings?.brand_name || 'Commissioning Tools';
  const domain = settings?.domain || 'cxatools.com';
  const logo = settings?.logo;

  if (!loading && user) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to log in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cx-page flex items-center justify-center px-4 py-10">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          {logo ? (
            <img src={logo} alt={brandName} className="h-12 w-12 rounded-2xl object-cover border border-[color:var(--cx-border)] shadow-[var(--cx-shadow-glow)]" />
          ) : (
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center shadow-[var(--cx-shadow-glow)]" style={{ background: 'var(--cx-brand-gradient)' }}>
              <LayoutGrid className="h-6 w-6 text-[#04120f]" />
            </div>
          )}
          <h1 className="mt-4 font-display text-2xl tracking-tight text-[color:var(--cx-fg)] text-center">{brandName}</h1>
          <p className="font-mono text-xs text-[color:var(--cx-muted)]">{domain} · commissioning portal</p>
        </div>

        <div className="cx-glass rounded-2xl shadow-[var(--cx-shadow-soft)]">
          <div className="p-6 pb-2">
            <h2 className="font-display text-xl text-[color:var(--cx-fg)]">Welcome back</h2>
            <p className="mt-1 text-sm text-[color:var(--cx-fg-muted)]">Sign in to access your tools.</p>
          </div>
          <form onSubmit={handleSubmit} className="p-6 pt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[color:var(--cx-fg-muted)]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--cx-muted)]" />
                <Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@cxatools.com" className="pl-9" data-testid="login-email-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[color:var(--cx-fg-muted)]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--cx-muted)]" />
                <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9" data-testid="login-password-input" />
              </div>
            </div>

            {error && <p className="text-sm text-[color:var(--destructive)]" data-testid="login-error-text">{error}</p>}

            <Button type="submit" disabled={submitting} className="w-full" data-testid="login-submit-button">
              {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Signing in…</>) : 'Sign in'}
            </Button>
          </form>
          <div className="px-6 pb-6 -mt-1 text-center text-sm text-[color:var(--cx-fg-muted)]">
            New here?{' '}
            <Link to="/register" data-testid="login-register-link" className="text-[color:var(--cx-primary)] hover:underline">Request access</Link>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-center gap-2 text-xs text-[color:var(--cx-muted)]">
          <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--cx-primary)]" />
          Private portal · access approved by an admin
        </div>
      </motion.div>
    </div>
  );
}
